import React, { useState } from 'react';
import { Users, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

const SubUserRow = ({ user, onToggle, onDelete, loading }) => {
  const isActive = user.status === 'active';
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <span className="text-blue-700 font-semibold text-sm">
              {(user.full_name || user.email || '?').charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900 text-sm">{user.full_name || '—'}</div>
            <div className="text-xs text-gray-400">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-5 py-3.5">
        {user.category_name ? (
          <span className="text-xs font-medium bg-blue-50 text-blue-700 rounded-full px-2.5 py-1">
            {user.category_name}
          </span>
        ) : (
          <span className="text-gray-300 text-xs">—</span>
        )}
      </td>
      <td className="px-5 py-3.5 text-xs text-gray-400">
        {user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : '—'}
      </td>
      <td className="px-5 py-3.5 text-right">
        <div className="flex items-center justify-end gap-1">
          {/* Phase 86AZ.19 — Read-only mode: when onToggle is not provided
              (sub-user viewing the team) just render a status badge. */}
          {onToggle ? (
            <Button
              data-testid={`toggle-subuser-${user.id}`}
              size="sm"
              variant="ghost"
              onClick={() => onToggle(user.id)}
              disabled={loading}
              className={`rounded-full gap-2 text-xs ${isActive ? 'text-emerald-600 hover:bg-emerald-50' : 'text-gray-400 hover:bg-gray-50'}`}
              title={isActive ? 'Pasif yap' : 'Aktif yap'}
            >
              {isActive
                ? <><ToggleRight className="h-4 w-4" /> Aktif</>
                : <><ToggleLeft className="h-4 w-4" /> Pasif</>
              }
            </Button>
          ) : (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-gray-400'}`} />
              {isActive ? 'Aktif' : 'Pasif'}
            </span>
          )}
          {/* Phase 86AZ.16 — Inline delete with a click-to-confirm step so
              the inviter can't nuke a sub-user by accident. The second
              click actually fires the destructive request. */}
          {onDelete && (
            confirmingDelete ? (
              <Button
                data-testid={`delete-subuser-confirm-${user.id}`}
                size="sm"
                variant="ghost"
                onClick={() => { onDelete(user.id); setConfirmingDelete(false); }}
                disabled={loading}
                className="rounded-full gap-1 text-xs text-white bg-red-600 hover:bg-red-700 px-2.5"
                title="Onayla & Sil"
              >
                <Trash2 className="h-3.5 w-3.5" /> Onayla
              </Button>
            ) : (
              <Button
                data-testid={`delete-subuser-${user.id}`}
                size="sm"
                variant="ghost"
                onClick={() => setConfirmingDelete(true)}
                disabled={loading}
                className="rounded-full text-red-500 hover:bg-red-50 px-2"
                title="Kullanıcıyı sil ve daveti iptal et"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )
          )}
          {confirmingDelete && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmingDelete(false)}
              disabled={loading}
              className="rounded-full text-xs text-gray-500 hover:bg-gray-100 px-2"
              title="Vazgeç"
            >
              ✕
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export const SubUserList = ({ subUsers, onToggle, onDelete, loading }) => {
  const active = subUsers.filter((u) => u.status === 'active');
  const inactive = subUsers.filter((u) => u.status !== 'active');

  if (subUsers.length === 0) {
    return (
      <div className="text-center py-14">
        <Users className="h-12 w-12 text-gray-200 mx-auto mb-3" />
        <p className="text-gray-400 text-sm">Henüz alt kullanıcı oluşturulmamış.</p>
      </div>
    );
  }

  const TableSection = ({ title, users, badgeColor }) => (
    <>
      <tr>
        <td colSpan={4} className="px-5 pt-5 pb-2">
          <span className={`text-xs font-semibold uppercase tracking-wider ${badgeColor}`}>{title} ({users.length})</span>
        </td>
      </tr>
      {users.map((u) => (
        <SubUserRow key={u.id} user={u} onToggle={onToggle} onDelete={onDelete} loading={loading} />
      ))}
    </>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Kullanıcı</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Kategori</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Katılım</th>
            <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Durum</th>
          </tr>
        </thead>
        <tbody>
          {active.length > 0 && <TableSection title="Aktif Kullanıcılar" users={active} badgeColor="text-emerald-600" />}
          {inactive.length > 0 && <TableSection title="Pasif Kullanıcılar" users={inactive} badgeColor="text-gray-400" />}
        </tbody>
      </table>
    </div>
  );
};
